/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/// <reference types="bingmaps" />

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classname from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import {
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    IPayLoad,
    isMobile,
    ITelemetryContent,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    NodeTag,
    TelemetryConstant,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import { ArrayExtensions, IDistributorInfo } from '@msdyn365-commerce-modules/retail-actions';
import { OrgUnit, TenderType } from '@msdyn365-commerce/retail-proxy';
import { IDistributorSelectorData } from './distributor-selector.data';
import { IDistributorSelectorProps } from './distributor-selector.props.autogenerated';
import {
    DistributorSelectorButton,
    DistributorSelectorDoNotShowAgain,
    DistributorSelectorCountMessage,
    DistributorSelectorTermsOfService,
    DistributorSelectorWaiting,
    DistributorEmptyMessage
} from './components/distributor-selector-small-components';
import { DistributorSelectorLines } from './components/distributor-selector-lines';
import { DistributorSelectorSearchForm } from './components/distributor-selector-search-form';

/**
 * Distributor State Interface.
 */
export interface IDistributorState {
    isSearchInProgress: boolean;
    searchTerm?: string;
    isLocationDisabled?: boolean;
    showDistributorDetails?: boolean;
    showWarningDialog?: boolean;
    warningDialogState?: boolean;
    currentSelectedDistributor?: IDistributorInfo;
    distributorId: number | undefined;
    distributorDetailsFromPopup: boolean;
    filteredPaymentTypeId: string;
}

export interface ICurrentDistributor {
    DistributorName?: string;
    DistributorId?: number;
}

/**
 * Distributor View Props Interface.
 */
export interface IDistributorSelectorViewProps extends IDistributorSelectorProps<IDistributorSelectorData> {
    state: IDistributorState;

    // Current distributor button props
    currentDistributorContainer?: IModuleProps;
    currentDistributorButton: React.ReactNode;

    // Containers:
    moduleProps: IModuleProps;
    bodyContainerProps: INodeProps;
    footerContainerProps: INodeProps;
    headerContainerProps: INodeProps;

    // Distributor warning Props
    distributorWarningHeaderProps?: React.ReactNode;
    distributorWarningContentHeadingProps?: React.ReactNode;
    distributorWarningContent?: React.ReactNode;
    distributorWarningDoNotShowAgain?: React.ReactNode;
    distributorWarningContinue?: React.ReactNode;
    distributorWarningCancel?: React.ReactNode;
    distributorWarningButtonWrapper: INodeProps;

    // Distributor selector Props
    distributorSelectorHeaderProps?: React.ReactNode;
    // Components
    distributorCountMessage?: React.ReactNode;
    searchForm?: React.ReactNode;
    spinner?: React.ReactNode;
    noDistributorsMessage?: React.ReactNode;
    mapContainerProps?: INodeProps;
    maps?: React.ReactNode;
    isMobileView?: boolean;
    distributorDetailsModal?: INodeProps;
    bodyWrapperProps?: INodeProps;
    distributorsResultContainerProps: INodeProps;
    distributorsResultWrapperProps: INodeProps;
    distributorLinesViewProps?: React.ReactNode;
    terms?: React.ReactNode;

    // distributor details modal popup
    showDistributorDetailsPopup: boolean;
    distributorDetailsModalPopup: IModuleProps;
    distributorDetailsModalHeader: INodeProps;
    distributorDetailsHeaderProps: React.ReactNode;
    distributorDetailsModalBody: INodeProps;
    distributorDetailsModalBodyContainer: INodeProps;

    distributorDetailsLocation: React.ReactNode;
    distributorDetailsPrimaryContact: React.ReactNode;
    distributorDetailsDeliveryMethods: React.ReactNode;
    distributorDetailsViewAllDetails: React.ReactNode;
    distributorDetailsModalFooter: INodeProps;
    distributorDetailsChangeDistributor: React.ReactNode;

    // distributor details slider
    distributorDetailsSliderWrapper: INodeProps;
    distributorDetailsSliderHeaderContainer: INodeProps;
    distributorDetailsSliderFooterContainer: INodeProps;
    distributorDetailsSliderBodyContainer: INodeProps;
    distributorDetailsSliderDistributorName: React.ReactNode;
    distributorDetailsSliderHeaderContent: React.ReactNode;
    distributorDetailsSliderInformationWrapper: INodeProps;
    distributorDetailsSliderCurrentDistributorTitle: React.ReactNode;
    distributorDetailsSliderSelectDistributor: React.ReactNode;
}

/**
 * ILocationHours Interface.
 */
export interface ILocationHours {
    openFrom?: string;
    openTo?: string;
}

/**
 *
 * DistributorSelector component
 * @extends {React.Component<IDistributorSelectorProps<IDistributorSelectorData>>}
 */
@observer
class DistributorSelector extends React.Component<IDistributorSelectorProps<IDistributorSelectorData>, IDistributorState> {
    private readonly telemetryContent: ITelemetryContent;
    private mainClass = 'ms-distributor-selector';
    private headerClass = 'ms-header__current-distributor';
    private readonly currentDistributorBtnRef: React.RefObject<HTMLButtonElement> = React.createRef();
    private currentDistributor?: ICurrentDistributor;

    public constructor(props: IDistributorSelectorProps<IDistributorSelectorData>) {
        super(props);
        this.state = {
            isSearchInProgress: true,
            searchTerm: '',
            showWarningDialog: false,
            showDistributorDetails: false,
            distributorId: undefined,
            distributorDetailsFromPopup: false,
            filteredPaymentTypeId: ''
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.currentDistributor = this.getCurrentDistributor();
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                actionContext: {
                    requestContext: { channel }
                },
                request: { user }
            }
        } = this.props;

        if (this.props.data.distributorSelectorStateManager.result) {
            await this._getAndUpdateDistributorList();
            this.checkUserStateAndOpenDialog(user);
        }

        reaction(
            () => this.props.data.distributorSelectorStateManager.result,
            async () => {
                await this._getAndUpdateDistributorList();
                this.checkUserStateAndOpenDialog(user);
            }
        );

        reaction(
            () => this.props.data.distributorSelectorStateManager.result?.loadMapApi,
            () => {
                this.props.data.distributorSelectorStateManager.result?.loadMapApi({
                    key: channel?.BingMapsEnabled ? channel.BingMapsApiKey : '',
                    lang: this.props.context.actionContext.requestContext.locale,
                    market: this.props.context.actionContext.requestContext.channel?.ChannelCountryRegionISOCode
                });
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            config: {
                className,
                distributorSelectorHeading,
                distributorWarningDialogHeading,
                distributorWarningContent,
                termsOfServiceLink,
                distributorDetailsToBeShown,
                selectDistributorText,
                doNotShowAgainCheckbox,
                noDistributorFoundText
            },
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            },
            context: {
                request: { user }
            },
            slots: { maps: mapSlot },
            resources
        } = this.props;
        const { showWarningDialog } = this.state;
        const currentDistributorId = this.currentDistributor?.DistributorId;
        const currentDistributorName = this.currentDistributor?.DistributorName;
        const headerText = currentDistributorName ? currentDistributorName : resources.headerCurrentDistributorText;

        let distributorInfoList = distributorSelectorStateManager?.distributorList;
        const paymentTypes = this.getUniquePaymentTypes(distributorInfoList);

        const hasMapSlot = ArrayExtensions.hasElements(mapSlot);
        const isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';
        const selectedDistributorId = distributorSelectorStateManager?.selectedDistributorId;

        const distributorDetails = this.state.showDistributorDetails
            ? this.getDistributorDetails(this.state.distributorId)
            : this.getDistributorDetails(currentDistributorId);
        const isCurrentDistributorSelected = this.state.distributorId === currentDistributorId;
        const shouldOpenDistributorSelectorDialog = !!distributorSelectorStateManager?.isDistributorSelectorDialogOpen;
        const shouldOpenDistributorDetailPopup = !!(distributorSelectorStateManager?.isDistributorDetailPopupOpen && distributorDetails);
        const shouldDisplayList = distributorSelectorStateManager?.listMapViewState?.displayList;
        if (this.state.filteredPaymentTypeId && distributorInfoList) {
            distributorInfoList = this.filterDistributorsByPaymentTypes(distributorInfoList, this.state.filteredPaymentTypeId);
        }
        const sortedDistributorList = distributorInfoList ? this._sortDistributor(distributorInfoList, currentDistributorId) : [];

        const canShowLocationView =
            (distributorSelectorStateManager?.distributorSelectorContext?.showMapViewLink === undefined ||
                distributorSelectorStateManager.distributorSelectorContext.showMapViewLink) &&
            distributorSelectorStateManager?.isMapModuleLoaded &&
            hasMapSlot;

        const showDistributorDetailsOnMap = distributorSelectorStateManager?.showDistributorDetailsOnMap;

        // @ts-ignore
        const viewProps: IDistributorSelectorViewProps = {
            ...(this.props as IDistributorSelectorProps<IDistributorSelectorData>),
            state: this.state,
            maps: hasMapSlot ? mapSlot[0] : undefined,
            showDistributorDetailsPopup: shouldOpenDistributorDetailPopup,
            currentDistributorContainer: this.shouldShowDistributors(user)
                ? {
                      tag: 'div' as NodeTag,
                      moduleProps: this.props,
                      className: `${this.headerClass}-container`
                  }
                : undefined,
            currentDistributorButton: (
                <button
                    className={`${this.headerClass}-btn msc-btn`}
                    color='secondary'
                    aria-label={resources.headerCurrentDistributorAriaLabel}
                    onClick={this.handleDistributorSelectorClick}
                    ref={this.currentDistributorBtnRef}
                >
                    <span className={`${this.headerClass}-text`}>{headerText}</span>
                </button>
            ),
            moduleProps: {
                tag: Modal,
                moduleProps: this.props,
                className: classname(hasMapSlot ? `${this.mainClass} maps` : `${this.mainClass}`, className),
                autoFocus: true,
                fade: true,
                isOpen: shouldOpenDistributorSelectorDialog,
                'aria-label': showWarningDialog ? distributorWarningDialogHeading : distributorSelectorHeading,
                onClosed: this._closeDistributorSelectorDialog,
                horizontalPosition: 'center',
                verticalPosition: 'center',
                toggle:
                    currentDistributorId || !ArrayExtensions.hasElements(distributorInfoList)
                        ? this._closeDistributorSelectorDialog
                        : undefined
            },
            isMobileView: isMobileDevice,
            headerContainerProps: {
                tag: ModalHeader,
                className: `${this.mainClass}-modal-header`,
                toggle:
                    currentDistributorId || !ArrayExtensions.hasElements(distributorInfoList)
                        ? this._closeDistributorSelectorDialog
                        : undefined
            },
            distributorSelectorHeaderProps: distributorSelectorHeading && (
                <Msdyn365.Text
                    className='msc-modal__title'
                    {...distributorSelectorHeading}
                    tag='h2'
                    text={distributorSelectorHeading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            distributorWarningHeaderProps: distributorWarningDialogHeading && (
                <Msdyn365.Text
                    className='msc-modal__title'
                    {...distributorWarningDialogHeading}
                    tag='h2'
                    text={distributorWarningDialogHeading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            distributorWarningContent: distributorWarningContent && (
                <Msdyn365.RichTextComponent
                    className='msc-modal__warning-content'
                    {...distributorWarningContent}
                    text={distributorWarningContent}
                    editProps={{ onEdit: this.handleWarningTextChange, requestContext: this.props.context.request }}
                />
            ),
            footerContainerProps: {
                tag: ModalFooter,
                className: classname(`${this.mainClass}__modal-footer`, this.state.showWarningDialog ? 'distributor-warning-footer' : '')
            },
            bodyContainerProps: {
                tag: ModalBody,
                className: classname(`${this.mainClass}__modal-body`, this.state.showWarningDialog ? 'distributor-warning' : '')
            },
            bodyWrapperProps: {
                tag: 'div' as NodeTag,
                className: `${this.mainClass}__body_wrapper`
            },
            distributorsResultContainerProps: {
                tag: 'div' as NodeTag,
                className: classname(`${this.mainClass}__distributor_container`, shouldDisplayList ? 'show' : 'hide')
            },
            distributorsResultWrapperProps: {
                tag: 'div' as NodeTag,
                className: classname(`${this.mainClass}__distributors_wrapper`, !showDistributorDetailsOnMap ? 'hide' : '')
            },
            mapContainerProps: hasMapSlot
                ? {
                      tag: 'div' as NodeTag,
                      className: classname(`${this.mainClass}__map_container`, !shouldDisplayList ? 'show' : 'hide')
                  }
                : undefined,
            distributorCountMessage: (
                <DistributorSelectorCountMessage
                    distributorCountMessage={resources.distributorCountMessage}
                    distributors={sortedDistributorList}
                />
            ),
            searchForm: (
                <DistributorSelectorSearchForm
                    mainClassName={this.mainClass}
                    shouldDisplayList={shouldDisplayList}
                    telemetryContent={this.telemetryContent}
                    viewMapText={resources.viewMapText}
                    viewListText={resources.viewListText}
                    showLocatorView={canShowLocationView}
                    paymentFilterByHeading={resources.paymentFilterByHeading}
                    paymentFilterMenuHeading={resources.paymentFilterMenuHeading}
                    paymentTypes={paymentTypes}
                    showPaymentTypeFilter={false}
                    filteredPaymentType={this.state.filteredPaymentTypeId}
                    onToggleListMapViewState={this.toggleListMapViewState}
                    onPaymentFilterSelected={this.onFilteredPaymentType}
                />
            ),
            spinner: <DistributorSelectorWaiting />,
            distributorLinesViewProps: ArrayExtensions.hasElements(distributorInfoList) ? (
                <DistributorSelectorLines
                    mainClassName={this.mainClass}
                    distributors={sortedDistributorList}
                    resources={resources}
                    selectedDistributorId={selectedDistributorId}
                    currentDistributorId={currentDistributorId}
                    shouldDisplayList={shouldDisplayList}
                    telemetryContent={this.telemetryContent}
                    isLocationDisabled={this.state.isLocationDisabled}
                    distributorDetailsToBeShown={distributorDetailsToBeShown}
                    selectDistributorText={selectDistributorText}
                    shouldShowIndex={true}
                    onSetAsCurrentDistributor={this._setCurrentDistributor}
                    onDistributorSelected={this._setSelectedDistributor}
                    onViewAllDistributorDetails={this.onViewAllDistributorDetails}
                />
            ) : (
                undefined
            ),
            noDistributorsMessage: <DistributorEmptyMessage emptyLocationsText={noDistributorFoundText} />,
            terms: (
                <DistributorSelectorTermsOfService
                    link={termsOfServiceLink}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                />
            ),
            distributorWarningButtonWrapper: {
                tag: 'div' as NodeTag,
                className: `${this.mainClass}__footer-button-wrapper`
            },
            distributorWarningDoNotShowAgain: doNotShowAgainCheckbox && (
                <DistributorSelectorDoNotShowAgain onChange={this.onChangeForDoNotShow} doNotShowAgainMessage={"Don't show again"} />
            ),
            distributorWarningContinue: (
                <DistributorSelectorButton
                    text='Continue'
                    onClick={this.continueButtonClick}
                    className={`${this.mainClass}_footer-continue`}
                />
            ),
            distributorWarningCancel: (
                <DistributorSelectorButton text='Cancel' onClick={this.cancelButtonClick} className={`${this.mainClass}_footer-cancel`} />
            ),
            ...this.renderDistributorDetailsViewProps(distributorDetails),
            ...this.renderDistributorSliderViewProps(distributorDetails, isCurrentDistributorSelected)
        };
        return this.props.renderView(viewProps);
    }

    public toggleListMapViewState = async (): Promise<void> => {
        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;
        await distributorSelectorStateManager?.toggleListMapViewState();
    };

    /**
     * Method to be called on the click on don't show again checkbox.
     */
    public onChangeForDoNotShow = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ warningDialogState: event.target.checked });
    };

    public onSelectDistributor = async (): Promise<void> => {
        if (this.state.distributorId) {
            await this._setCurrentDistributor(this.state.distributorId);
        }
    };

    public onChangeDistributorClick = async (): Promise<void> => {
        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;
        if (distributorSelectorStateManager) {
            // Set selected distributor id
            await distributorSelectorStateManager.setSelectedDistributorId(undefined);

            if (distributorSelectorStateManager.isDistributorDetailPopupOpen) {
                // close the detail popup
                await distributorSelectorStateManager.closeDistributorDetailPopup();
            }

            if (distributorSelectorStateManager.isDistributorSelectorDialogOpen) {
                this.setState({ distributorId: undefined, showDistributorDetails: false, distributorDetailsFromPopup: false });
            } else {
                // Open the distributor selector modal
                await distributorSelectorStateManager.openDistributorSelectorDialog({
                    parentElementRef: this.currentDistributorBtnRef
                });
            }
        }
    };

    public onDistributorCloseButtonClick = async () => {
        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;
        if (distributorSelectorStateManager) {
            await distributorSelectorStateManager.closeDistributorDetailPopup();
        }
    };

    /**
     * Method to be called on the click on the continue button.
     */
    public continueButtonClick = async (): Promise<void> => {
        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            },
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;

        const currentDistributor = this.state.currentSelectedDistributor;

        if (this.state.warningDialogState) {
            distributorSelectorStateManager?.hideWarningMessage(cookies.isConsentGiven());
        }

        await this._closeDistributorSelectorDialog();
        this.redirectToDistributorURL(currentDistributor);
    };

    /**
     * Method to be called on click on the cancel button.
     */
    public cancelButtonClick = async (): Promise<void> => {
        await this._closeDistributorSelectorDialog();
        this.setState({ warningDialogState: false, showWarningDialog: false, currentSelectedDistributor: undefined });
    };

    public backToSearchClick = async (): Promise<void> => {
        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;
        this.setState({ showDistributorDetails: false, distributorId: undefined });
        await distributorSelectorStateManager?.setSelectedDistributorId(undefined);
    };

    public onViewAllDistributorDetailsFromPopup = async (): Promise<void> => {
        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;

        if (distributorSelectorStateManager) {
            if (!this.currentDistributor?.DistributorId) {
                return;
            }

            // close the detail popup
            await distributorSelectorStateManager.closeDistributorDetailPopup();

            // Open the distributor selector modal
            await distributorSelectorStateManager.openDistributorSelectorDialog({
                parentElementRef: this.currentDistributorBtnRef
            });

            // Set selected distributor id
            await distributorSelectorStateManager.setSelectedDistributorId(this.currentDistributor?.DistributorId);

            this.setState({
                distributorId: this.currentDistributor?.DistributorId,
                showDistributorDetails: true,
                distributorDetailsFromPopup: true
            });
        }
    };

    public onViewAllDistributorDetails = async (distributorId: number | undefined): Promise<void> => {
        if (!distributorId) {
            return;
        }
        this.setState({ distributorId: distributorId, showDistributorDetails: true });
    };

    private getCurrentDistributor = (): ICurrentDistributor | undefined => {
        const {
            context: {
                request: {
                    url: { requestUrl },
                    user
                }
            }
        } = this.props;

        // We show the distributor selector module when the user is signed-in.
        if (user.isAuthenticated) {
            const distributorList = this.props.data.distributorList?.result;

            const currentDistributor = distributorList?.find(distributor =>
                distributor.MarketSettings?.localeItems.some(
                    locale => requestUrl.href.toLowerCase().indexOf(locale.localeBaseUrl.toLowerCase()) >= 0
                )
            );

            if (currentDistributor) {
                return {
                    DistributorId: currentDistributor.OrgUnit?.RecordId,
                    DistributorName: currentDistributor.OrgUnit?.OrgUnitName
                };
            } else {
                return undefined;
            }
        }
        return undefined;
    };

    private getUniquePaymentTypes = (distributors?: IDistributorInfo[]) => {
        const uniquePaymentTypes = new Set<TenderType>();
        distributors?.forEach(distributor => {
            distributor.PaymentTypes?.forEach(paymentType => {
                uniquePaymentTypes.add(paymentType);
            });
        });
        return [...uniquePaymentTypes];
    };

    /**
     * The method to be called for filtering store locations based on the user's selected filters.
     * @param distributors - Distributors List.
     * @param filteredPaymentTypeId - Filter selected by the user.
     * @returns -Filtered distributors.
     */
    private readonly filterDistributorsByPaymentTypes = (distributors: IDistributorInfo[], filteredPaymentTypeId: string) => {
        return distributors.filter(distributor => {
            return distributor?.PaymentTypes?.some(paymentType => {
                return paymentType.TenderTypeId === filteredPaymentTypeId;
            });
        });
    };

    /**
     * Redirect user to the distributor selector site.
     * @param currentDistributor - Current distributor details
     */
    private redirectToDistributorURL = (currentDistributor: IDistributorInfo | undefined) => {
        const { telemetry } = this.props;
        const marketSettings = currentDistributor?.MarketSettings;

        // Check if the current locale is present.
        const currentLocale = marketSettings?.localeItems.find(localeItem => localeItem.locale === this.props.context.request.locale);
        let distributorURL = '';
        if (currentLocale) {
            distributorURL = currentLocale.localeBaseUrl;
        } else {
            // Check if the default locale is set.
            let defaultLocale = marketSettings?.localeItems.find(localeItem => localeItem.isDefaultLocale);
            if (!defaultLocale) {
                defaultLocale = ArrayExtensions.hasElements(marketSettings?.localeItems) ? marketSettings?.localeItems[0] : undefined;
            } else {
                distributorURL = defaultLocale.localeBaseUrl;
            }
        }

        if (distributorURL) {
            if (!distributorURL.startsWith('https://')) {
                distributorURL = `https://${distributorURL}`;
            }
            window.location.href = distributorURL;
        } else if (telemetry) {
            telemetry.debug('redirectToDistributorURL: Distributor URL is empty');
        }
    };

    /**
     * Handle click on the distributor selector.
     * @returns : Promise void.
     */
    private handleDistributorSelectorClick = async () => {
        const distributorSelectorStateManager = this.props.data.distributorSelectorStateManager?.result;

        if (this.currentDistributor?.DistributorId) {
            if (distributorSelectorStateManager?.isDistributorDetailPopupOpen) {
                distributorSelectorStateManager?.closeDistributorDetailPopup();
            } else {
                await distributorSelectorStateManager?.openDistributorDetailPopup({
                    parentElementRef: this.currentDistributorBtnRef
                });
            }
            return;
        }
    };

    /**
     * Filter distributors by payment types.
     * @param tenderTypeId -Delivery Code.
     */
    private readonly onFilteredPaymentType = (tenderTypeId: string) => {
        this.setState({ filteredPaymentTypeId: tenderTypeId });
    };

    /**
     * Creates the distributor details view props.
     * @param distributorDetails: Distributor details
     * @returns : View props for the distributor details.
     */
    private renderDistributorDetailsViewProps = (distributorDetails: IDistributorInfo | undefined) => {
        const {
            config: { switchDistributorText, distributorDetailsToBeShown },
            resources
        } = this.props;
        const distributorAddress = distributorDetails?.OrgUnit?.OrgUnitAddress?.FullAddress;
        const distributorPhoneNo = distributorDetails?.OrgUnit?.OrgUnitAddress?.Phone;
        const deliveryMethods = distributorDetails?.OrgUnitDeliveryOptions?.DeliveryOptions;

        const distributorDetailsViewProps = {
            distributorDetailsModalPopup: {
                tag: 'div',
                moduleProps: this.props,
                className: classname(`${this.mainClass}__details_dialog`)
            },
            distributorDetailsModalHeader: {
                tag: 'div',
                className: `${this.mainClass}__dialog__header`
            },
            distributorDetailsHeaderProps: (
                <>
                    <span className={`${this.mainClass}__dialog__heading`}>{distributorDetails?.OrgUnit?.OrgUnitName || ''}</span>
                    <button
                        className='close-popup'
                        aria-label={resources.closeDistributorDetailPopupButtonAriaLabel}
                        onClick={this.onDistributorCloseButtonClick}
                    />
                </>
            ),
            distributorDetailsModalBody: {
                tag: 'div',
                className: `${this.mainClass}__dialog__body`
            },
            distributorDetailsModalBodyContainer: {
                tag: 'div',
                className: `${this.mainClass}__dialog__container`
            },
            distributorDetailsLocation: (this.state.showDistributorDetails ||
                (distributorDetailsToBeShown && distributorDetailsToBeShown.indexOf('location') > -1)) && (
                <div className={`${this.mainClass}__details`}>
                    <span className='heading'>{resources.locationHeadingText}</span>
                    {distributorAddress && (
                        <div className='location-section'>
                            <span className='location-icon'></span>
                            <span className='location'>{distributorAddress}</span>
                        </div>
                    )}
                </div>
            ),
            distributorDetailsPrimaryContact: (this.state.showDistributorDetails ||
                (distributorDetailsToBeShown && distributorDetailsToBeShown?.indexOf('primaryContact') > -1)) && (
                <div className={`${this.mainClass}__details`}>
                    <span className='heading'>{resources.primaryContactText}</span>
                    {distributorPhoneNo && (
                        <div className='contact-section'>
                            <span className='contact-icon'></span>
                            <span className='contact'>{distributorPhoneNo}</span>
                        </div>
                    )}
                </div>
            ),
            distributorDetailsDeliveryMethods: (this.state.showDistributorDetails ||
                (distributorDetailsToBeShown && distributorDetailsToBeShown.indexOf('deliveryMethod') > -1)) && (
                <div className={`${this.mainClass}__details`}>
                    <span className='heading'>{resources.deliveryMethodHeadingText}</span>
                    {ArrayExtensions.hasElements(deliveryMethods) && (
                        <div className='delivery-methods-section'>
                            <>
                                {deliveryMethods?.map(deliveryMethod => {
                                    return (
                                        <span key={deliveryMethod.RecordId} className='delivery-method'>
                                            {deliveryMethod.Description}
                                        </span>
                                    );
                                })}
                            </>
                        </div>
                    )}
                </div>
            ),
            distributorDetailsViewAllDetails: (
                <a className={`${this.mainClass}__view-all-distributor-details`} onClick={this.onViewAllDistributorDetailsFromPopup}>
                    {resources.viewAllDistributorText}
                </a>
            ),
            distributorDetailsChangeDistributor: (
                <button onClick={this.onChangeDistributorClick} className='change-distributor'>
                    {
                        <Msdyn365.Text
                            text={switchDistributorText}
                            editProps={{ onEdit: this.handleChangeDistributorTextChange, requestContext: this.props.context.request }}
                        />
                    }
                </button>
            ),
            distributorDetailsModalFooter: {
                tag: 'div',
                className: `${this.mainClass}__dialog__footer`
            },
            distributorDetailsSliderWrapper: {
                tag: 'div',
                className: classname(
                    `${this.mainClass}__details-slider-wrapper`,
                    this.state.showDistributorDetails ? 'show' : '',
                    this.state.distributorDetailsFromPopup ? 'no-header' : ''
                )
            }
        };
        return distributorDetailsViewProps;
    };

    private renderDistributorSliderViewProps = (
        distributorDetails: IDistributorInfo | undefined,
        isCurrentDistributorSelected: boolean
    ) => {
        const {
            resources,
            config: { selectDistributorText }
        } = this.props;
        const payLoad: IPayLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.CurrentDistributor);
        const currentDistributorAttributes = getTelemetryAttributes(this.telemetryContent, payLoad);

        const distributorSliderViewProps = {
            distributorDetailsSliderDistributorName: (
                <h5 className={`${this.mainClass}__details-slider-name`}>{distributorDetails?.OrgUnit?.OrgUnitName}</h5>
            ),
            distributorDetailsSliderHeaderContent: (
                <a className='back-to-search' onClick={this.backToSearchClick}>
                    {resources.distributorSliderBackToSearch}
                </a>
            ),
            distributorDetailsSliderHeaderContainer: {
                tag: 'div',
                className: `${this.mainClass}__details-slider-heading`
            },
            distributorDetailsSliderBodyContainer: {
                tag: 'div',
                className: `${this.mainClass}__details-slider-body`
            },
            distributorDetailsSliderFooterContainer: {
                tag: 'div',
                className: `${this.mainClass}__details-slider-footer`
            },
            distributorDetailsSliderInformationWrapper: {
                tag: 'div',
                className: `${this.mainClass}__details-slider-info-wrapper`
            },
            distributorDetailsSlidercurrentDistributorTitle: isCurrentDistributorSelected && (
                <div className='current-distributor'>
                    <div className='content'>{resources.currentDistributorText}</div>
                </div>
            ),
            distributorDetailsSliderSelectDistributor: !isCurrentDistributorSelected && (
                <button
                    className={`${this.mainClass}__select-distributor`}
                    aria-label={selectDistributorText}
                    {...currentDistributorAttributes}
                    onClick={this.onSelectDistributor}
                >
                    {
                        <Msdyn365.Text
                            text={selectDistributorText}
                            editProps={{ onEdit: this.handleSelectDistributorTextChange, requestContext: this.props.context.request }}
                        />
                    }
                </button>
            )
        };
        return distributorSliderViewProps;
    };

    /**
     * Method to be called on heading change.
     * @param event -Content Edit Event.
     */
    private handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        if (this.state.showWarningDialog) {
            this.props.config.distributorWarningDialogHeading = event.target.value;
        } else {
            this.props.config.distributorSelectorHeading = event.target.value;
        }
    };

    /**
     * Method to be called on warning text change.
     * @param event -Content Edit Event.
     */
    private handleWarningTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.distributorWarningContent = event.target.value;
    };

    /**
     * Method to be called on term of service text change.
     * @param event -Content Edit Event.
     */
    private handleLinkTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        if (this.props.config.termsOfServiceLink) {
            this.props.config.termsOfServiceLink.linkText = event.target.value;
        }
    };

    /**
     * Method to be called on distributor text change.
     * @param event -Content Edit Event.
     */
    private handleSelectDistributorTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        if (this.props.config.selectDistributorText) {
            this.props.config.selectDistributorText = event.target.value;
        }
    };

    /**
     * Method to be called on link text change.
     * @param event -Content Edit Event.
     */
    private handleChangeDistributorTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        if (this.props.config.switchDistributorText) {
            this.props.config.switchDistributorText = event.target.value;
        }
    };

    private shouldShowDistributors = (user: Msdyn365.IRequestContextUser): boolean => {
        const isOboRequest = Msdyn365.isOboRequest(this.props.context.request);
        const isOBOAccountSelected = isOboRequest && !!this.props.context.request.cookies.getAccountSelectionCookie();
        return isOBOAccountSelected || (!isOboRequest && !!user.isSignedIn);
    };

    /**
     * If the user is authenticated but has no current distributor, open the distributor selector dialog.
     * @param user - IRequestContextUser
     */
    private checkUserStateAndOpenDialog = (user: Msdyn365.IRequestContextUser) => {
        if (!this.currentDistributor && this.shouldShowDistributors(user)) {
            const distributorSelectorStateManager = this.props.data.distributorSelectorStateManager.result;
            distributorSelectorStateManager?.openDistributorSelectorDialog({
                parentElementRef: this.currentDistributorBtnRef
            });
        }
    };

    /**
     * Get the distributor details from the distributorId
     * @param distributorId - Distributor Id
     * @returns - Returns the distributor info.
     */
    private getDistributorDetails = (distributorId: number | undefined): IDistributorInfo | undefined => {
        const {
            data: {
                distributorList: { result: distributorList }
            }
        } = this.props;

        return distributorList?.find(distributor => distributor.OrgUnit?.RecordId === distributorId);
    };

    /**
     * Method to be called updating the distributor list.
     */
    private readonly _getAndUpdateDistributorList = async (): Promise<void> => {
        const {
            data: {
                distributorList: { result: distributorList }
            }
        } = this.props;

        this.setState({ isSearchInProgress: true });

        if (distributorList) {
            await this._updateDistributorListInStateManager(distributorList);
        }

        this.setState({ isSearchInProgress: false });
    };

    /**
     * Method to update the distributor state manager.
     * @param distributorList - distributor Info Object.
     * @returns Void promise.
     */
    private readonly _updateDistributorListInStateManager = async (distributorList: IDistributorInfo[]): Promise<void> => {
        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            },
            telemetry
        } = this.props;

        if (!distributorSelectorStateManager || !distributorList) {
            return;
        }

        const currentDistributorId = this.currentDistributor?.DistributorId;
        const sortedDistributorList = this._sortDistributor(distributorList, currentDistributorId);

        // When serializing the distributor selector state manager, functions get stripped.
        if (distributorSelectorStateManager.setDistributorsList) {
            await distributorSelectorStateManager.setDistributorsList(sortedDistributorList).catch((error: Error) => {
                if (telemetry) {
                    telemetry.error(error.message);
                    telemetry.debug('Unable to Update distributor state');
                }
            });
        }
    };

    /**
     * Method will sort all distributor.
     * @param distributors - Distributor List.
     * @param currentDistributorId - Distributor Id.
     * @returns List of distributor.
     */
    private _sortDistributor(distributors: IDistributorInfo[], currentDistributorId: number | undefined): IDistributorInfo[] {
        const isCurrentDistributor = (value: IDistributorInfo) => currentDistributorId && value.OrgUnit?.RecordId === currentDistributorId;

        return [...distributors.filter(isCurrentDistributor), ...distributors.filter(distributor => !isCurrentDistributor(distributor))];
    }

    /**
     * Method to be called closing the distributor selector dialog.
     * @returns Void promise.
     */
    private readonly _closeDistributorSelectorDialog = async (): Promise<void> => {
        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;
        if (distributorSelectorStateManager) {
            distributorSelectorStateManager.distributorSelectorContext?.parentElementRef?.current?.focus();
            // Update the distributor map visibility to true.
            distributorSelectorStateManager.updateDistributorMapVisibility(true);
            await distributorSelectorStateManager.setSelectedDistributorId(undefined);
            await distributorSelectorStateManager.closeDistributorSelectorDialog();
            this.setState({
                searchTerm: '',
                showWarningDialog: false,
                currentSelectedDistributor: undefined,
                distributorDetailsFromPopup: false,
                showDistributorDetails: false,
                distributorId: undefined
            });
        }
    };

    /**
     * On Selecting distributor.
     * @param distributorId - Distributor Id.
     */
    private readonly _setSelectedDistributor = async (orgUnit: OrgUnit | undefined): Promise<void> => {
        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;

        if (!distributorSelectorStateManager || !orgUnit) {
            return;
        }

        await distributorSelectorStateManager.setSelectedDistributorId(orgUnit.RecordId);
    };

    /**
     * On Selecting Current Distributor.
     * @param distributorId - Distributor Id.
     */
    private readonly _setCurrentDistributor = async (distributorId: number | undefined): Promise<void> => {
        if (!distributorId) {
            return;
        }

        const {
            data: {
                distributorSelectorStateManager: { result: distributorSelectorStateManager }
            }
        } = this.props;

        if (!distributorSelectorStateManager) {
            return;
        }
        const distributorList = distributorSelectorStateManager?.distributorList;
        const currentDistributor = distributorList?.find(distributor => distributor.OrgUnit?.RecordId === distributorId);
        if (distributorSelectorStateManager.hideDistributorWarning || !this.currentDistributor) {
            this.redirectToDistributorURL(currentDistributor);
            await this._closeDistributorSelectorDialog();
        } else {
            this.setState({ showWarningDialog: true, currentSelectedDistributor: currentDistributor });
        }
    };
}

export default DistributorSelector;
